/**
 * `run_code` (worker/lib/engine/tools/code.ts), run by the page on every
 * platform it runs on: the web, the desktop app and Android.
 *
 * The code runs in a frame of its own (web/public/runner.html), sandboxed so
 * it has no origin: it cannot reach Assist's API, the person's session or
 * this page's storage, and its policy gives it no network. Python is
 * Pyodide, which the frame asks this page for file by file; the page fetches
 * each from `PYODIDE_URL` and nowhere else, and the browser keeps them for
 * next time under this origin.
 */

import { CODE_LANGUAGES, PYODIDE_URL, type CodeLanguage } from '@wire';
import type { PendingCall } from './api';

/** What one run came to, as the frame reports it. */
export interface RunOutcome {
  /** Null when it did not finish. */
  exitCode: number | null;
  stdout: string;
  stderr: string;
  /** The value the code ended on, as Python's `repr` or console.log shows it. */
  value: string | null;
  /** Stopped at the frame's limit once it had started. */
  timedOut?: boolean;
  /** Stopped because the person stopped the reply. */
  stopped?: boolean;
  /** The runner, not the code, failed: Pyodide did not load. */
  error?: string;
}

/** How long a run's code may take, as the frame counts it. */
const RUN_SECONDS = 30;
/** The longest a run may take all told, Pyodide's first download included. */
const OVERALL_MS = 120_000;
/** The most of what a run printed that goes back to the model. */
const MAX_RESULT_CHARS = 16_000;

/** Whether this page can run code at all. */
export const canRunCode = (): boolean =>
  typeof window !== 'undefined' && typeof Worker === 'function' && typeof WebAssembly === 'object';

// ── the frame ───────────────────────────────────────────────────────────────

let frame: Promise<Window> | null = null;
let runs = 0;
const waiting = new Map<number, (outcome: RunOutcome) => void>();

/** One of Pyodide's files, for the frame; nothing outside `PYODIDE_URL`. */
async function sendFile(win: Window, id: number, raw: unknown): Promise<void> {
  let url: string;
  try {
    url = new URL(String(raw)).href;
  } catch {
    url = '';
  }
  if (!url.startsWith(PYODIDE_URL)) {
    win.postMessage({ type: 'file', id, ok: false, error: 'Not allowed.' }, '*');
    return;
  }
  try {
    const res = await fetch(url, { credentials: 'omit' });
    const bytes = await res.arrayBuffer();
    const mime = res.headers.get('content-type') ?? 'application/octet-stream';
    win.postMessage({ type: 'file', id, ok: res.ok, status: res.status, mime, bytes, error: `HTTP ${res.status}` }, '*', [bytes]);
  } catch (err) {
    win.postMessage({ type: 'file', id, ok: false, error: err instanceof Error ? err.message : 'Offline.' }, '*');
  }
}

/** The frame, made the first time it is needed and kept. */
function runner(): Promise<Window> {
  frame ??= new Promise<Window>((resolve, reject) => {
    const el = document.createElement('iframe');
    el.src = '/runner.html';
    el.title = 'Code runner';
    el.hidden = true;
    el.setAttribute('sandbox', 'allow-scripts');
    el.setAttribute('aria-hidden', 'true');
    const late = setTimeout(() => {
      frame = null;
      el.remove();
      reject(new Error('The code runner did not load.'));
    }, 20_000);
    window.addEventListener('message', (e: MessageEvent) => {
      const win = el.contentWindow;
      if (!win || e.source !== win) return;
      const m = e.data as { type?: string; id?: number; url?: unknown } & Partial<RunOutcome>;
      if (m.type === 'ready') {
        clearTimeout(late);
        resolve(win);
      } else if (m.type === 'file' && typeof m.id === 'number') {
        void sendFile(win, m.id, m.url);
      } else if (m.type === 'done' && typeof m.id === 'number') {
        waiting.get(m.id)?.({
          exitCode: typeof m.exitCode === 'number' ? m.exitCode : null,
          stdout: String(m.stdout ?? ''),
          stderr: String(m.stderr ?? ''),
          value: typeof m.value === 'string' ? m.value : null,
          ...(m.timedOut ? { timedOut: true } : {}),
          ...(m.stopped ? { stopped: true } : {}),
          ...(m.error ? { error: String(m.error) } : {})
        });
      }
    });
    document.body.appendChild(el);
  });
  return frame;
}

/** Starts Pyodide loading, so a run that follows need not wait for it. */
export function warmPython(): void {
  if (!canRunCode()) return;
  runner().then(
    (win) => win.postMessage({ type: 'warm', base: PYODIDE_URL }, '*'),
    () => {}
  );
}

const EMPTY: RunOutcome = { exitCode: null, stdout: '', stderr: '', value: null };

export async function runCode(language: CodeLanguage, code: string, signal?: AbortSignal): Promise<RunOutcome> {
  let win: Window;
  try {
    win = await runner();
  } catch (err) {
    return { ...EMPTY, error: err instanceof Error ? err.message : 'The code runner did not load.' };
  }
  const id = ++runs;
  return new Promise<RunOutcome>((resolve) => {
    const settle = (outcome: RunOutcome) => {
      clearTimeout(late);
      signal?.removeEventListener('abort', stop);
      waiting.delete(id);
      resolve(outcome);
    };
    const stop = () => {
      win.postMessage({ type: 'stop', id }, '*');
      settle({ ...EMPTY, stopped: true });
    };
    const late = setTimeout(() => {
      win.postMessage({ type: 'stop', id }, '*');
      settle({ ...EMPTY, error: 'It did not start in time.' });
    }, OVERALL_MS);
    if (signal?.aborted) return stop();
    signal?.addEventListener('abort', stop, { once: true });
    waiting.set(id, settle);
    win.postMessage({ type: 'run', id, language, code, base: PYODIDE_URL }, '*');
  });
}

// ── what the model is given ─────────────────────────────────────────────────

/**
 * A Python traceback without Pyodide's own frames, which come before the
 * code's in every one and say nothing about it.
 */
export function tidyTraceback(text: string): string {
  const out: string[] = [];
  let skipping = false;
  for (const line of text.split('\n')) {
    if (/^ {2}File "/.test(line)) skipping = /^ {2}File "\/lib\/python[^"]*\/_pyodide\//.test(line);
    else if (skipping && !line.startsWith('    ')) skipping = false;
    if (!skipping) out.push(line);
  }
  return out.join('\n');
}

/** Kept from both ends, so where it started and where it ended both show. */
function cut(text: string, room: number): string {
  if (text.length <= room) return text;
  const half = Math.floor(room / 2);
  return `${text.slice(0, half)}\n[…${text.length - room} characters cut…]\n${text.slice(-half)}`;
}

/** A run as the model reads it, in the shape of run_command's results. */
export function resultText(o: RunOutcome, language: CodeLanguage): { content: string; isError: boolean } {
  if (o.error) {
    return { content: `${language === 'python' ? 'Python' : 'JavaScript'} could not run here: ${o.error}`, isError: true };
  }
  const half = Math.floor(MAX_RESULT_CHARS / 2);
  const stderr = language === 'python' ? tidyTraceback(o.stderr) : o.stderr;
  const text = [
    o.timedOut ? `Stopped after ${RUN_SECONDS} seconds.` : `Exit code ${o.exitCode ?? 1}`,
    o.stdout ? `stdout:\n${cut(o.stdout, half)}` : '',
    stderr ? `stderr:\n${cut(stderr, half)}` : '',
    o.value !== null ? `value:\n${cut(o.value, 2_000)}` : ''
  ]
    .filter(Boolean)
    .join('\n\n');
  return { content: text, isError: !!o.timedOut };
}

// ── a reply's calls ─────────────────────────────────────────────────────────

/** The calls a waiting reply left for the page. */
export const pageCalls = (pending: readonly PendingCall[]): PendingCall[] =>
  pending.filter((c) => c.where === 'page' && c.needs === 'result');

/** Names a model may use for the two languages. */
const LANGUAGE_NAMES: Record<string, CodeLanguage> = { py: 'python', python3: 'python', js: 'javascript', node: 'javascript' };

/** A call's language and code, or why they cannot be run. */
export function readRun(args: string): { language: CodeLanguage; code: string } | { invalid: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(args);
  } catch {
    return { invalid: 'The arguments were not valid JSON.' };
  }
  const { language, code } = (parsed ?? {}) as { language?: unknown; code?: unknown };
  const named = typeof language === 'string' ? language.trim().toLowerCase() : '';
  const lang = LANGUAGE_NAMES[named] ?? named;
  if (!(CODE_LANGUAGES as readonly string[]).includes(lang)) return { invalid: 'The language must be python or javascript.' };
  if (typeof code !== 'string' || !code.trim()) return { invalid: 'There was no code to run.' };
  return { language: lang as CodeLanguage, code };
}

/** Runs the calls in order, as `continue` takes their results. A stop
 *  leaves the rest unrun, and none of them are sent. */
export async function runPageCalls(
  calls: readonly PendingCall[],
  signal: AbortSignal
): Promise<Array<{ callId: string; content: string; isError?: boolean }>> {
  const results: Array<{ callId: string; content: string; isError?: boolean }> = [];
  for (const call of calls) {
    if (signal.aborted) break;
    const run = readRun(call.arguments);
    if ('invalid' in run) {
      results.push({ callId: call.id, content: run.invalid, isError: true });
      continue;
    }
    const outcome = await runCode(run.language, run.code, signal);
    if (outcome.stopped) break;
    const { content, isError } = resultText(outcome, run.language);
    results.push({ callId: call.id, content, ...(isError ? { isError: true } : {}) });
  }
  return results;
}
